/**
 * The dialog a `One of` roadmap item opens
 * ([#6077](https://github.com/CodeinPlace/cip/issues/6077)): one card per
 * assignment with its splash picture (or its icon), title, one-line tagline
 * and an optional note course staff wrote, the finished one marked, and the
 * student's pick returned. Shared by the todo list and the
 * PAI maps, so both roadmaps offer the same choice. Built on sweetalert2 with
 * React content, like the section-switch dialog.
 */
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import {FaCheck, FaChevronRight} from 'react-icons/fa';
import type {TodoIconOverride} from 'course/application/firebase/models/applicationData';
import {TODO_ICON_OVERRIDE_ICONS} from 'course/application/general/components/CipxTodoList';

const ReactSwal = withReactContent(Swal);

/** One assignment the student may pick. */
export interface OneOfChoice {
  /** The Math IDE assignment id. */
  id: string;
  /** Its title, as the assignment names itself. */
  title: string;
  /**
   * One line on what the student will do there, from the assignment's
   * metadata. Omitted shows the title alone.
   */
  tagline?: string;
  /**
   * The card's icon, a key of the course's closed icon set. Omitted or
   * unknown draws the rocket every Math Assignment wears on the roadmap.
   */
  icon?: string;
  /**
   * The assignment's splash picture, from its metadata. Set, it is drawn in
   * place of the icon; omitted, the card draws the icon.
   */
  picture?: {src: string; alt: string};
  /**
   * A short line course staff wrote about this assignment, from the
   * assignment's metadata. Omitted or blank shows nothing.
   */
  note?: string;
  /** Whether the student has already finished it. */
  isComplete: boolean;
}

/** Translates one string of the `course` namespace to plain text. */
type Translate = (text: string, options: {forceString: true}) => string;

const STYLE = `
  .one-of-popup {
    padding-bottom: 1.6em;
  }
  .one-of-popup .swal2-title {
    padding-top: 1.1em;
    font-size: 1.55rem;
  }
  .one-of-blurb {
    margin: 0 auto 18px;
    max-width: 34em;
    color: #4b5563;
    font-size: 1rem;
    line-height: 1.5;
  }
  .one-of-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin: 0;
  }
  .one-of-choice {
    display: flex;
    align-items: center;
    gap: 16px;
    width: 100%;
    padding: 14px 16px;
    border: 1.5px solid #e5e7eb;
    border-radius: 16px;
    background: #fff;
    color: #111827;
    font: inherit;
    text-align: left;
    cursor: pointer;
    box-shadow: 0 1px 2px rgba(17, 24, 39, 0.05);
    transition: transform 0.12s ease, box-shadow 0.12s ease, border-color 0.12s ease;
  }
  .one-of-choice:hover {
    border-color: #316cf4;
    box-shadow: 0 6px 18px rgba(49, 108, 244, 0.16);
    transform: translateY(-1px);
  }
  .one-of-choice:focus-visible {
    outline: 3px solid #316cf4;
    outline-offset: 2px;
  }
  .one-of-choice[data-done='true'] {
    border-color: #ff7ffc;
  }
  .one-of-choice[data-done='true']:hover {
    box-shadow: 0 6px 18px rgba(255, 127, 252, 0.28);
  }
  .one-of-icon {
    flex: none;
    display: grid;
    place-items: center;
    width: 56px;
    height: 56px;
    border-radius: 16px;
    background: #eaf0ff;
    color: #316cf4;
    font-size: 26px;
  }
  .one-of-choice[data-done='true'] .one-of-icon {
    background: #ff7ffc;
    color: #fff;
  }
  .one-of-icon[data-picture='true'] {
    position: relative;
    width: 112px;
    height: 112px;
    overflow: hidden;
    background: #0b1020;
  }
  .one-of-icon[data-picture='true'] img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  .one-of-badge {
    position: absolute;
    right: 6px;
    bottom: 6px;
    display: grid;
    place-items: center;
    width: 26px;
    height: 26px;
    border-radius: 999px;
    background: #ff7ffc;
    color: #fff;
    font-size: 14px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.35);
  }
  .one-of-text {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 3px;
  }
  .one-of-name {
    font-size: 1.08rem;
    font-weight: 700;
    line-height: 1.25;
  }
  .one-of-tagline {
    color: #4b5563;
    font-size: 0.92rem;
    line-height: 1.4;
  }
  .one-of-note {
    color: #a21caf;
    font-size: 0.84rem;
    font-weight: 600;
    line-height: 1.4;
  }
  .one-of-end {
    flex: none;
    display: flex;
    align-items: center;
    color: #9ca3af;
    font-size: 0.9rem;
  }
  .one-of-done {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    border-radius: 999px;
    background: #ff7ffc;
    color: #fff;
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }
  @media (max-width: 480px) {
    .one-of-choice {
      gap: 12px;
      padding: 12px;
    }
    .one-of-icon {
      width: 48px;
      height: 48px;
      font-size: 22px;
    }
    .one-of-icon[data-picture='true'] {
      width: 76px;
      height: 76px;
    }
    .one-of-end .one-of-chevron {
      display: none;
    }
  }
`;

/** The icon a card draws: the choice's own, else the roadmap's rocket. */
function iconFor(icon: string | undefined) {
  const known = (Object.keys(TODO_ICON_OVERRIDE_ICONS) as TodoIconOverride[]).find(
    key => key === icon,
  );
  return TODO_ICON_OVERRIDE_ICONS[known ?? 'rocket'];
}

function OneOfBody({
  title,
  blurb,
  choices,
  doneLabel,
  onPick,
}: {
  title: string;
  blurb: string;
  choices: readonly OneOfChoice[];
  doneLabel: string;
  onPick: (id: string) => void;
}) {
  return (
    <>
      <style>{STYLE}</style>
      <p className="one-of-blurb">{blurb}</p>
      <div className="one-of-list" role="group" aria-label={title}>
        {choices.map(choice => (
          <button
            key={choice.id}
            type="button"
            className="one-of-choice"
            data-done={choice.isComplete ? 'true' : 'false'}
            aria-label={
              choice.isComplete ? `${choice.title}, ${doneLabel}` : undefined
            }
            onClick={() => onPick(choice.id)}
          >
            {choice.picture !== undefined ? (
              <span className="one-of-icon" data-picture="true" aria-hidden="true">
                <img src={choice.picture.src} alt="" />
                {choice.isComplete && (
                  <span className="one-of-badge">
                    <FaCheck />
                  </span>
                )}
              </span>
            ) : (
              <span className="one-of-icon" aria-hidden="true">
                {choice.isComplete ? <FaCheck /> : iconFor(choice.icon)}
              </span>
            )}
            <span className="one-of-text">
              <span className="one-of-name">{choice.title}</span>
              {choice.tagline !== undefined && choice.tagline.trim() !== '' && (
                <span className="one-of-tagline">{choice.tagline.trim()}</span>
              )}
              {choice.note !== undefined && choice.note.trim() !== '' && (
                <span className="one-of-note">{choice.note.trim()}</span>
              )}
            </span>
            <span className="one-of-end" aria-hidden="true">
              {choice.isComplete ? (
                <span className="one-of-done">{doneLabel}</span>
              ) : (
                <FaChevronRight className="one-of-chevron" />
              )}
            </span>
          </button>
        ))}
      </div>
    </>
  );
}

/**
 * Offers the choices and waits for a pick.
 *
 * @param args.title - The roadmap item's title, the dialog's heading.
 * @param args.description - The item's description, shown under the heading.
 *   Omitted or blank: a generic line says any one choice completes the step.
 * @param args.choices - The assignments, in the authored order. A single
 *   choice is returned at once without a dialog.
 * @param args.t - The `course` translation function.
 * @return The picked assignment id, or `null` when the dialog is dismissed
 *   or there is nothing to choose from.
 * @throws Never.
 */
export async function chooseOneOfAssignment({
  title,
  description,
  choices,
  t,
}: {
  title: string;
  description?: string;
  choices: readonly OneOfChoice[];
  t: Translate;
}): Promise<string | null> {
  if (choices.length === 0) return null;
  if (choices.length === 1) return choices[0].id;

  let picked: string | null = null;
  const blurb =
    description !== undefined && description.trim() !== ''
      ? description.trim()
      : t(
          'Each of these is a different assignment with the same goal. Pick whichever you are most curious about. Finishing one completes this step, and you can come back for the others any time.',
          {forceString: true},
        );
  const doneLabel = t('Done', {forceString: true});

  await ReactSwal.fire({
    title,
    html: (
      <OneOfBody
        title={title}
        blurb={blurb}
        choices={choices}
        doneLabel={doneLabel}
        onPick={id => {
          picked = id;
          ReactSwal.close();
        }}
      />
    ),
    width: 'min(600px, 94vw)',
    customClass: {popup: 'one-of-popup'},
    showConfirmButton: false,
    showCloseButton: true,
    focusConfirm: false,
    didOpen: popup => {
      // Start on the one already finished, so a returning student lands on
      // their own work; otherwise on the first.
      const buttons = popup.querySelectorAll<HTMLButtonElement>(
        '.one-of-choice',
      );
      const doneAt = choices.findIndex(choice => choice.isComplete);
      buttons[doneAt === -1 ? 0 : doneAt]?.focus();
    },
  });
  return picked;
}
